const _excluded = ["children", "className", "style", "container", "content", "variant", "showArrow", "offset", "rootClose", "placement", "show", "target", "overlayRef", "getTargetProps", "onHide", "onShow", "trigger", "triggerTarget", "delayShow", "delayHide", "data-testid", "renderTarget", "renderArrow", "renderBody"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { noop, useForwardedRef } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import Portal from '@adsk/alloy-react-portal';
import theme from '@adsk/alloy-react-theme';
import { TRIGGER_EVENTS, TRIGGER_TARGETS } from './consts';
import useTooltip from './useTooltip';
const getOffsetSize = offset => {
  if (typeof offset === 'number') {
    return offset;
  }
  return offset.mainAxis;
};
const Tooltip = _ref => {
  let {
      children,
      className,
      style,
      container = document.body,
      content,
      variant = Overlay.VARIANTS.DARK,
      showArrow = true,
      offset = {
        crossAxis: 0,
        mainAxis: 10
      },
      rootClose = true,
      placement: placementProp = Overlay.PLACEMENTS.TOP,
      show: showProp,
      target,
      overlayRef,
      getTargetProps = () => ({}),
      onHide: onHideProp = noop,
      onShow: onShowProp = noop,
      trigger = TRIGGER_EVENTS.HOVER,
      triggerTarget = TRIGGER_TARGETS.CHILDREN,
      delayShow = 300,
      delayHide = 0,
      'data-testid': dataTestid,
      renderTarget = props => /*#__PURE__*/React.createElement(Overlay.Target, props),
      renderArrow = props => /*#__PURE__*/React.createElement(Overlay.Arrow, props),
      renderBody = props => /*#__PURE__*/React.createElement(Overlay.Body, props)
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const targetRef = useForwardedRef(target || null);
  const {
    show,
    placement,
    interactive,
    getReferenceProps,
    getFloatingProps,
    getArrowProps
  } = useTooltip(_objectSpread({
    enableCloseOnClickOutside: rootClose,
    placement: placementProp,
    show: showProp,
    trigger,
    triggerTarget,
    delayShow,
    delayHide,
    onHide: onHideProp,
    onShow: onShowProp,
    targetRef,
    overlayRef,
    offset,
    variant
  }, props));
  const offsetSize = getOffsetSize(offset);
  const contentStyle = _objectSpread({}, variant === Overlay.VARIANTS.DARK && {
    a: {
      color: theme.tokens.colors.text.link.inverse.value
    }
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderTarget(_objectSpread(_objectSpread({
    wrapper: 'div',
    shouldWrapChildren: true
  }, getReferenceProps(getTargetProps())), {}, {
    children
  })), show && Boolean(content) && /*#__PURE__*/React.createElement(Portal, {
    container: container
  }, /*#__PURE__*/React.createElement(Overlay.Container, _extends({
    placement: placement,
    "data-testid": dataTestid
  }, getFloatingProps()), showArrow && renderArrow(getArrowProps()), renderBody({
    className,
    style: [contentStyle, style],
    variant,
    children: content
  }), interactive && /*#__PURE__*/React.createElement(Overlay.Blank, {
    placement: placement,
    size: offsetSize
  }))));
};
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  /*
   * className to add to the tooltip overlay
   */
  className: PropTypes.string,
  /*
   * style to tooltip overlay
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /*
   * The contents to show within the popover.
   * hidden when no content available
   */
  content: PropTypes.node,
  /*
   * The content of the element to which the tooltip is applied
   */
  children: PropTypes.node.isRequired,
  /*
   * Tooltip variant
   */
  variant: PropTypes.oneOf(Object.values(Overlay.VARIANTS)),
  /*
   * Whether the showing is triggered by click or mouseover
   */
  trigger: PropTypes.oneOf(Object.values(TRIGGER_EVENTS)),
  /*
   * The Overlay target (either the children or the tooltip itself)
   */
  triggerTarget: PropTypes.oneOf(Object.values(TRIGGER_TARGETS)),
  /*
   * Prop getter to apply props to the trigger/target element.
   * Use this instead of applying props directly
   */
  getTargetProps: PropTypes.func,
  /*
   * Overlay position according to Overlay placements.
   */
  placement: PropTypes.oneOf(Object.values(Overlay.PLACEMENTS)),
  /*
   * Target element ref (get/set).
   */
  target: PropTypes.object,
  /*
   * Overlay ref to overlay dom element.
   */
  overlayRef: PropTypes.any,
  /*
   * Specify whether the overlay should trigger `onHide` when the user clicks outside the overlay.
   */
  rootClose: PropTypes.bool,
  /*
   * Whether the popover is showed or not. Should be sent only for
   * controlled elements
   */
  show: PropTypes.bool,
  /*
   * Callback to be called on show
   */
  onShow: PropTypes.func,
  /*
   * Callback to be called on hide
   */
  onHide: PropTypes.func,
  /*
   * Delay before show in milliseconds
   */
  delayShow: PropTypes.number,
  /*
   * Delay before hide in milliseconds
   */
  delayHide: PropTypes.number,
  /*
   * Whether to not have the arrow - show and default to true
   */
  showArrow: PropTypes.bool,
  /* Options that modify how the tooltip overlay can shift (see floating-ui documentation https://floating-ui.com/docs/shift) */
  shiftArgs: PropTypes.any,
  /* Options that modify how the tooltip overlay can flip (see floating-ui documentation https://floating-ui.com/docs/flip) */
  flipArgs: PropTypes.any,
  /* Options that modify how the tooltip overlay can hide (when target is hidden) (see floating-ui documentation https://floating-ui.com/docs/hide) */
  hideArgs: PropTypes.any
};
export default Object.assign(Tooltip, {
  Arrow: Overlay.Arrow,
  Target: Overlay.Target,
  Body: Overlay.Body,
  VARIANTS: Overlay.VARIANTS,
  PLACEMENTS: Overlay.PLACEMENTS,
  TRIGGER_EVENTS,
  TRIGGER_TARGETS
});
//# sourceMappingURL=Tooltip.js.map